/* Hybrid API client.
 *
 * Routes each domain to the right backend:
 *   - products / categories / auth  → real FastAPI (Render)
 *   - cart / checkout / orders /
 *     profile / enquiries /
 *     notifications                 → mock-adapter (localStorage)
 *
 * Auth mutations also mirror the resulting session into the mock store
 * so cart/orders/enquiries can attribute writes to the logged-in user.
 *
 *   VITE_MOCK_MODE=1    → force everything onto the mock (offline demo).
 *   otherwise           → hybrid mode (default).
 */

import { api as realApi, setToken as realSetToken } from "@/lib/api";
import { mockApi } from "@/api/mock-adapter";
import { DEMO_USERS } from "@/data/mocks/users";
import * as store from "@/data/mocks/storage";

export const isMock = import.meta.env.VITE_MOCK_MODE === "1";

/* ---------- session mirror -----------------------------------------------
 * The mock-adapter reads `store.get("session")` to attribute carts/orders/
 * enquiries to a user. When we authenticate via the real backend, we copy
 * the relevant bits across so the mock side has a session it recognises.
 *
 * We map the real user.email back to one of the seeded mock user IDs
 * (u-customer / u-clinician / u-retailer). For unseeded users we fall back
 * to the real user.id so cart/order writes are still partitioned correctly.
 */
function mirrorSessionFromMe(me, tokens) {
  if (!me) return;
  const seedMatch = DEMO_USERS.find(
    (u) => u.email.toLowerCase() === (me.email || "").toLowerCase()
  );
  const mockUserId = seedMatch ? seedMatch.id : me.id;
  store.set("session", {
    user_id: mockUserId,
    role: me.role,
    email: me.email,
    token: tokens?.access_token || null,
    refresh: tokens?.refresh_token || null,
    expires: Date.now() + 60 * 60 * 1000,
  });
}

function clearSession() {
  store.remove("session");
}

/* ---------- hybrid auth --------------------------------------------------
 * Wraps the real backend's auth endpoints, then mirrors session into mock.
 */
const hybridAuth = {
  signup: async (body) => {
    const res = await realApi.auth.signup(body);
    if (res?.access_token) {
      realSetToken(res.access_token);
      // /signup returns tokens + user; some shapes return just tokens.
      const me = res.user || (await realApi.auth.me().catch(() => null));
      mirrorSessionFromMe(me, res);
    }
    return res;
  },

  login: async (body) => {
    const res = await realApi.auth.login(body);
    if (res?.access_token) {
      realSetToken(res.access_token);
      const me = await realApi.auth.me().catch(() => null);
      mirrorSessionFromMe(me, res);
    }
    return res;
  },

  refresh: async (refresh_token) => {
    const res = await realApi.auth.refresh(refresh_token);
    if (res?.access_token) realSetToken(res.access_token);
    return res;
  },

  logout: async (refresh_token) => {
    try { await realApi.auth.logout(refresh_token); } catch (_) { /* best-effort */ }
    realSetToken(null);
    clearSession();
    try { window.localStorage.removeItem("nethrasap.refresh"); } catch (_) {}
    return { ok: true };
  },

  me: async () => {
    // /auth/me is the source of truth — also re-mirror so a page reload
    // restores the mock session for users who refreshed mid-session.
    const me = await realApi.auth.me();
    if (me) mirrorSessionFromMe(me, null);
    return me;
  },
};

/* ---------- assembly ------------------------------------------------------ */

export const api = isMock
  ? mockApi
  : {
      get base() { return realApi.base; },
      health: realApi.health,

      // Real (Render-backed)
      products:   realApi.products,
      categories: realApi.categories,
      auth:       hybridAuth,

      // Mock (localStorage-backed) — these domains have no real endpoints yet
      cart:          mockApi.cart,
      checkout:      mockApi.checkout,
      orders:        mockApi.orders,
      profile:       mockApi.profile,
      enquiries:     mockApi.enquiries,
      notifications: mockApi.notifications,
    };

/** Re-export setToken so hooks.ts can stay backend-agnostic. */
export function setToken(token) {
  realSetToken(token);
  if (!token) clearSession();
}
